/**
 * Auth Configuration
 *
 * In production, these values are loaded from a runtime config.json
 * hosted alongside the app on S3/CloudFront.
 * In development, loads from environment-specific config files.
 */

import type { AuthConfig } from "@fmc-digital/core-auth";

// Runtime config interface
export interface RuntimeConfig {
  okta: {
    issuer: string;
    clientId: string;
    redirectUri: string;
    scopes: string[];
    useProxy?: boolean;
  };
  api: {
    baseUrl: string;
    baseUrlV2?: string; // V2 base URL for registration APIs
    baseUrlt1?: string; // Alternative base URL for APIs (e.g., WCM/CMS endpoint)
    xApiKey?: string;
  };
  /** Okta base URL for API calls (injected at runtime) */
  oktaUrl: string;
  googleRecaptchaSiteKey?: string;
  /** URL for external registration app (used when user clicks Register) */
  registerNowUrl?: string;
  /** Single landing URL for “Download the App” (app marketing or store listing) */
  mobileAppDownloadUrl?: string;
  /** Base URL for translations CDN (e.g., CloudFront distribution) */
  translationsBaseUrl?: string;
  /** Base URL for content CDN (images, assets, etc.) */
  cdnRootUrl?: string;
  /** FullStory organization ID for session recording and analytics */
  fullStoryOrgId?: string;
  /** Dashboard URL - Okta OIDC endpoint that redirects to dashboard after authentication */
  dashboardUrl?: string;
  /** Google Tag Manager container ID (e.g. GTM-XXXXXXX) */
  googleTagManagerCode?: string;
  /** When true, VWO FME is used for feature flags (e.g. registration routing on login). */
  isVWOEnabled?: boolean;
  /** VWO account ID for FME `init` */
  vwoAccountId?: number;
  /** VWO FME SDK key (inject via deploy secrets; do not commit production values). */
  vwoSDKKey?: string;
  /** Proxy URL for VWO FME traffic */
  vwoProxyUrl?: string;
  /** Optional VWO environment label (e.g. DEV, UAT) */
  vwoEnv?: string;
  /** OAuth issuer URL for SSO redirect flow (may differ from okta.issuer for different authorization server) */
  oauthIssuer?: string;
  /** OAuth client ID for SSO redirect flow (typically same as okta.clientId) */
  oauthClientId?: string;
  /** Customer portal callback URL for SSO redirect after authentication */
  dashboardRedirectUri?: string;
  /** Scopes for /v1/authorize (implicit) — must match Okta AS policy + app assignment */
  oauthAuthorizeScope?: string;
  /** Qualtrics API base URL for survey response submission */
  qualtricsUrl?: string;
  /** Qualtrics API token (X-API-TOKEN header) */
  qualtricsAPIKey?: string;
  /** Survey ID for post-registration NPS feedback */
  registrationNpsSurveyId?: string;
  /** Allow dynamic access to config properties */
  [key: string]: unknown;
}

// Environment-specific config file type (flat structure from JSON files)
interface EnvironmentConfig {
  environment?: string;
  // Flat structure properties (like customer-portal-ui)
  issuer?: string;
  clientId?: string;
  defaultRedirectURL?: string;
  daDefaultRedirectURL?: string;
  redirectUri?: string;
  scopes?: string[];
  baseUrl?: string;
  baseUrlV2?: string;
  baseUrlt1?: string;
  xApiKey?: string;
  // Legacy flat structure properties (for backward compatibility)
  clientIdWebAct?: string;
  issuerWebAct?: string;
  redirectUriWebAct?: string;
  baseUrlWebAct?: string;
  xAPIKey?: string;
  oktaUrl: string;
  googleRecaptchaSiteKey?: string;
  registerNowUrl?: string;
  mobileAppDownloadUrl?: string;
  translationsBaseUrl?: string;
  cdnRootUrl?: string;
  fullStoryOrgId?: string;
  dashboardUrl?: string;
  googleTagManagerCode?: string;
  isVWOEnabled?: boolean | string;
  vwoAccountId?: number | string;
  vwoSDKKey?: string;
  vwoProxyUrl?: string;
  vwoEnv?: string;
  oauthIssuer?: string;
  oauthClientId?: string;
  dashboardRedirectUri?: string;
  oauthAuthorizeScope?: string;
  useProxy?: boolean;
  qualtricsUrl?: string;
  qualtricsAPIKey?: string;
  registrationNpsSurveyId?: string;
  [key: string]: unknown;
}

/**
 * Valid environment values
 */
const VALID_ENVIRONMENTS = ["dev", "local", "i18nlocal", "test", "uat", "prod"] as const;

/**
 * Check if a value is a valid environment
 */
function isValidEnvironment(value: string): value is typeof VALID_ENVIRONMENTS[number] {
  return VALID_ENVIRONMENTS.includes(value as typeof VALID_ENVIRONMENTS[number]);
}

/**
 * Log import.meta.env contents for debugging
 */
function logImportMetaEnv(env: Record<string, unknown>): void {
  const envKeys = Object.keys(env);
  console.log("[Config] import.meta.env contents:", {
    MODE: env.MODE,
    VITE_ENV: env.VITE_ENV,
    DEV: env.DEV,
    PROD: env.PROD,
    BASE_URL: env.BASE_URL,
    allKeys: envKeys,
    allValues: envKeys.reduce(
      (acc, key) => {
        acc[key] = env[key];
        return acc;
      },
      {} as Record<string, unknown>,
    ),
  });
}

/**
 * Get environment from Vite VITE_ENV variable
 */
function getFromViteEnv(env: Record<string, unknown>): string | null {
  if (!env?.VITE_ENV) {
    return null;
  }
  const envValue = String(env.VITE_ENV);
  if (isValidEnvironment(envValue)) {
    console.log(`[Config] Environment detected from VITE_ENV: ${envValue}`);
    return envValue;
  }
  return null;
}

/**
 * Get environment from Vite MODE variable
 */
function getFromViteMode(env: Record<string, unknown>): string | null {
  if (!env?.MODE) {
    console.warn("[Config] Vite MODE not found in import.meta.env");
    return null;
  }
  const mode = String(env.MODE);
  console.log(`[Config] Vite MODE found: "${mode}" (type: ${typeof mode})`);
  
  if (isValidEnvironment(mode)) {
    console.log(`[Config] Environment detected from Vite MODE: ${mode}`);
    return mode;
  }
  
  if (mode === "development") {
    console.log(
      `[Config] Environment detected from Vite MODE (development -> dev): ${mode}`,
    );
    return "dev";
  }
  
  if (mode === "production") {
    console.log(
      `[Config] Environment detected from Vite MODE (production -> prod): ${mode}`,
    );
    return "prod";
  }
  
  console.warn(
    `[Config] Vite MODE "${mode}" is not recognized, expected: dev, test, uat, prod, development, or production`,
  );
  return null;
}

/**
 * Get environment from import.meta.env
 */
function getFromImportMetaEnv(): string | null {
  try {
    // Access import.meta.env directly (Vite makes this available)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const env = (import.meta as { env?: Record<string, unknown> }).env;

    if (!env) {
      console.warn("[Config] import.meta.env is not available");
      return null;
    }

    logImportMetaEnv(env);

    const viteEnv = getFromViteEnv(env);
    if (viteEnv) {
      return viteEnv;
    }

    return getFromViteMode(env);
  } catch (e) {
    console.warn("[Config] import.meta not available:", e);
    return null;
  }
}

/**
 * Get environment from process.env
 */
function getFromProcessEnv(): string | null {
  if (typeof process === "undefined" || !process.env) {
    return null;
  }

  if (process.env.VITE_ENV && isValidEnvironment(process.env.VITE_ENV)) {
    console.log(
      `[Config] Environment detected from process.env.VITE_ENV: ${process.env.VITE_ENV}`,
    );
    return process.env.VITE_ENV;
  }

  if (process.env.MODE && isValidEnvironment(process.env.MODE)) {
    console.log(
      `[Config] Environment detected from process.env.MODE: ${process.env.MODE}`,
    );
    return process.env.MODE;
  }

  if (process.env.NODE_ENV === "production") {
    console.log("[Config] Environment detected from NODE_ENV: prod");
    return "prod";
  }

  if (process.env.NODE_ENV === "development") {
    console.log("[Config] Environment detected from NODE_ENV: dev");
    return "dev";
  }

  return null;
}

/**
 * Get environment from URL search params
 */
function getFromUrlParams(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  const urlParams = new URLSearchParams(window.location.search);
  const envParam = urlParams.get("env");
  if (envParam && isValidEnvironment(envParam)) {
    console.log(`[Config] Environment detected from URL param: ${envParam}`);
    return envParam;
  }
  return null;
}

/**
 * Get environment name from various sources
 */
function getEnvironment(): string {
  console.log('[Config] Detecting environment...');
  
  const fromImportMeta = getFromImportMetaEnv();
  if (fromImportMeta) {
    console.log(`[Config] Environment detected from import.meta: ${fromImportMeta}`);
    return fromImportMeta;
  }

  const fromProcess = getFromProcessEnv();
  if (fromProcess) {
    console.log(`[Config] Environment detected from process.env: ${fromProcess}`);
    return fromProcess;
  }

  const fromUrl = getFromUrlParams();
  if (fromUrl) {
    console.log(`[Config] Environment detected from URL params: ${fromUrl}`);
    return fromUrl;
  }

  // Default to dev if no environment is detected
  const defaultEnv = "dev";
  
  console.warn(`[Config] No environment detected, defaulting to ${defaultEnv}`);
  console.warn(
    "[Config] To set environment, use: yarn env:dev, yarn env:test, yarn env:uat, or yarn env:prod",
  );
  return defaultEnv;
}

/**
 * Load environment-specific config file
 */
async function loadEnvironmentConfig(
  env: string,
): Promise<EnvironmentConfig | null> {
  try {
    // Use conditional imports based on environment
    let module: { default: EnvironmentConfig } | { default?: EnvironmentConfig };
    
    if (env === "local" || env === "i18nlocal") {
      module = await import("./environments/config.local.json");
    } else if (env === "dev") {
      module = await import("./environments/config.dev.json");
    } else if (env === "test") {
      module = await import("./environments/config.test.json");
    } else if (env === "uat") {
      module = await import("./environments/config.uat.json");
    } else if (env === "prod") {
      module = await import("./environments/config.prod.json");
    } else {
      console.warn(`[Config] Unknown environment: ${env}`);
      return null;
    }
    
    return module.default ?? (module as EnvironmentConfig);
  } catch (error) {
    console.warn(`Failed to load ${env} config file:`, error);
  }
  return null;
}

/**
 * Extract string value from flat config with legacy fallback
 */
function extractStringValue(
  flatValue: string | undefined,
  legacyValue?: string,
): string | undefined {
  if (typeof flatValue === "string") {
    return flatValue;
  }
  return legacyValue;
}

/**
 * Extract string value with default fallback
 */
function extractStringWithDefault(
  flatValue: string | undefined,
  legacyValue: string | undefined,
  defaultValue: string,
): string {
  return (
    extractStringValue(flatValue, legacyValue) ??
    defaultValue
  );
}

/**
 * Extract array value from flat config
 */
function extractArrayValue<T>(
  flatValue: T[] | undefined,
): T[] | undefined {
  if (Array.isArray(flatValue)) {
    return flatValue;
  }
  return undefined;
}

/**
 * Extract array value with default fallback
 */
function extractArrayWithDefault<T>(
  flatValue: T[] | undefined,
  defaultValue: T[],
): T[] {
  return extractArrayValue(flatValue) ?? defaultValue;
}

/**
 * Coerces config values to boolean. Accepts string forms on purpose: `config.template.json`
 * must use JSON strings for `{{ ... }}` placeholders so the file parses as JSON; deploy
 * substitution then yields `"true"` / `"false"` strings, which we normalize here.
 */
export function parseConfigBoolean(value: unknown): boolean {
  if (value === true || value === 1) {
    return true;
  }
  if (value === false || value === 0 || value === null || value === undefined || value === "") {
    return false;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
}

function parseOptionalVwoAccountId(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  const n = typeof value === "number" ? value : Number(String(value).trim());
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * Transform flat environment config to RuntimeConfig structure
 */
function transformConfig(envConfig: EnvironmentConfig): RuntimeConfig {
  console.log('[Config] Transforming config, raw envConfig:', envConfig);
  
  // Extract baseUrlt1 (no legacy fallback property exists)
  const baseUrlt1Value = extractStringValue(envConfig.baseUrlt1);
  const baseUrlV2Value = extractStringValue(envConfig.baseUrlV2);
  console.log('[Config] Extracted baseUrlt1:', baseUrlt1Value);
  console.log('[Config] Extracted baseUrlV2:', baseUrlV2Value);
  
  // Extract issuer (flat structure first, then legacy, then default)
  const issuer = extractStringWithDefault(
    envConfig.issuer,
    envConfig.issuerWebAct,
    "https://identity-dev.freedommortgage.com/oauth2/default",
  );
  
  // Extract clientId
  const clientId = extractStringWithDefault(
    envConfig.clientId,
    envConfig.clientIdWebAct,
    "",
  );
  
  // Extract redirectUri
  const redirectUri = extractStringWithDefault(
    envConfig.redirectUri,
    envConfig.redirectUriWebAct,
    window.location.origin + "/callback",
  );
  
  // Extract scopes
  const scopes = extractArrayWithDefault(
    envConfig.scopes,
    ["openid", "profile", "email", "offline_access"],
  );
  
  // Extract baseUrl
  const baseUrl = extractStringWithDefault(
    envConfig.baseUrl,
    envConfig.baseUrlWebAct,
    "https://gateway-dev.freedommortgage.com/api/v1/selfserv",
  );
  
  // Extract xApiKey
  const xApiKey = extractStringValue(
    envConfig.xApiKey,
    envConfig.xAPIKey,
  );
  
  // Extract translationsBaseUrl
  const translationsBaseUrl = extractStringValue(
    envConfig.translationsBaseUrl,
  );
  
  console.log('[Config] Extracted translationsBaseUrl:', translationsBaseUrl);
  
  // Extract cdnRootUrl
  const cdnRootUrl = extractStringValue(
    envConfig.cdnRootUrl,
  );
  
  console.log('[Config] Extracted cdnRootUrl:', cdnRootUrl);
  
  // Extract fullStoryOrgId
  const fullStoryOrgId = extractStringValue(
    envConfig.fullStoryOrgId,
  );

  const googleTagManagerCode = extractStringValue(
    envConfig.googleTagManagerCode,
  );
  
  // Extract dashboardUrl
  const dashboardUrl = extractStringValue(
    envConfig.dashboardUrl,
  );
  
  // Extract OAuth-specific config values
  const oauthIssuer = extractStringValue(
    envConfig.oauthIssuer,
  );
  
  const oauthClientId = extractStringValue(
    envConfig.oauthClientId,
  );
  
  const dashboardRedirectUri = extractStringValue(
    envConfig.dashboardRedirectUri,
  );

  const oauthAuthorizeScope = extractStringValue(
    envConfig.oauthAuthorizeScope,
  );

    const defaultRedirectURL = extractStringValue(envConfig.defaultRedirectURL);

    const daDefaultRedirectURL = extractStringValue(
      envConfig.daDefaultRedirectURL,
    );

  const isVWOEnabled = parseConfigBoolean(envConfig.isVWOEnabled);
  const vwoAccountId = parseOptionalVwoAccountId(envConfig.vwoAccountId);
  const vwoSDKKey = extractStringValue(envConfig.vwoSDKKey);
  const vwoProxyUrl = extractStringValue(envConfig.vwoProxyUrl);
  const vwoEnv = extractStringValue(envConfig.vwoEnv);

  const qualtricsUrl = extractStringValue(envConfig.qualtricsUrl);
  const qualtricsAPIKey = extractStringValue(envConfig.qualtricsAPIKey);
  const registrationNpsSurveyId = extractStringValue(envConfig.registrationNpsSurveyId);
  
  // Extract useProxy (default to undefined to allow fallback to false in getAuthConfig)
  const useProxy = envConfig.useProxy !== undefined ? Boolean(envConfig.useProxy) : undefined;
  
  return {
    okta: {
      issuer,
      clientId,
      redirectUri,
      scopes,
      useProxy,
    },
    api: {
      baseUrl,
      baseUrlV2: baseUrlV2Value,
      baseUrlt1: baseUrlt1Value,
      xApiKey,
    },
    oktaUrl: envConfig.oktaUrl,
    googleRecaptchaSiteKey: envConfig.googleRecaptchaSiteKey,
    registerNowUrl: envConfig.registerNowUrl,
    mobileAppDownloadUrl: extractStringValue(envConfig.mobileAppDownloadUrl),
    translationsBaseUrl,
    cdnRootUrl,
    fullStoryOrgId,
    dashboardUrl,
    googleTagManagerCode,
    oauthIssuer,
    oauthClientId,
    dashboardRedirectUri,
    oauthAuthorizeScope,
    isVWOEnabled,
    vwoAccountId,
    vwoSDKKey,
    vwoProxyUrl,
    vwoEnv,
    qualtricsUrl,
    qualtricsAPIKey,
    registrationNpsSurveyId,
    defaultRedirectURL,
    daDefaultRedirectURL,
  };
}

let config: RuntimeConfig | null = null;

/**
 * Fetch config.json from server (for production builds)
 * This matches CI/CD behavior where config.json is served from dist/
 */
async function fetchRuntimeConfig(): Promise<RuntimeConfig | null> {
  try {
    const response = await fetch("/config.json");
    if (!response.ok) {
      console.warn(`[Config] Failed to fetch config.json: ${response.status} ${response.statusText}`);
      return null;
    }
    const configData = await response.json();
    console.log(`[Config] ✓ Fetched config.json from server`);
    
    // Check if config has flat structure
    const hasFlatStructure = configData.issuer || configData.clientId || configData.baseUrl;
    
    if (hasFlatStructure) {
      // Transform flat structure to RuntimeConfig
      const flatConfig = configData as EnvironmentConfig;
      return transformConfig(flatConfig);
    }
    
    console.warn(`[Config] config.json from server missing required fields (issuer/clientId/baseUrl)`);
    return null;
  } catch (error) {
    console.warn(`[Config] Failed to fetch config.json from server:`, error);
    return null;
  }
}

/**
 * Apply local development overrides to config
 */
function applyLocalDevOverrides(cfg: RuntimeConfig): void {
  if (
    typeof window !== "undefined" &&
    window.location.hostname === "localhost" &&
    cfg.api?.baseUrlV2?.startsWith("http")
  ) {
    cfg.api.baseUrlV2 = "/api/v2/selfserv";
    console.log("[Config] Overriding baseUrlV2 with proxy path for local dev");
  }
}

/**
 * Fill missing config value from environment-specific config
 */
async function fillMissingConfigValue(
  configKey: keyof Pick<RuntimeConfig, "translationsBaseUrl" | "cdnRootUrl">,
  env: string,
): Promise<void> {
  if (config?.[configKey]) {
    return;
  }

  console.warn(`[Config] WARNING: ${configKey} is missing from server config, attempting to load from environment config`);

  const envConfig = await loadEnvironmentConfig(env);
  const value = envConfig?.[configKey];

  if (value && config) {
    config[configKey] = value;
    console.log(`[Config] ✓ Loaded ${configKey} from ${env} environment config: ${value}`);
  } else {
    const fallbackMsg = configKey === "translationsBaseUrl" ? "using local translations" : "using local images";
    console.warn(`[Config] WARNING: ${configKey} not found in ${env} config, will ${fallbackMsg}`);
  }
}

/**
 * Handle server-fetched runtime config
 */
async function handleServerConfig(runtimeConfig: RuntimeConfig): Promise<RuntimeConfig> {
  config = runtimeConfig;
  const envFromConfig = (runtimeConfig as RuntimeConfig & { environment?: string }).environment;
  (config as RuntimeConfig & { __env?: string }).__env = envFromConfig ?? "unknown";

  applyLocalDevOverrides(config);

  const env = envFromConfig ?? getEnvironment();
  await fillMissingConfigValue("translationsBaseUrl", env);
  await fillMissingConfigValue("cdnRootUrl", env);

  console.log(
    `[Config] ✓ Config loaded from server, environment: ${envFromConfig ?? "unknown"}, baseUrl: ${config?.api?.baseUrl}, translationsBaseUrl: ${config?.translationsBaseUrl ?? "none (using local)"}`,
  );
  return config;
}

/**
 * Handle static environment-based config loading
 */
async function handleStaticConfig(env: string): Promise<RuntimeConfig> {
  console.log(`[Config] Runtime config.json not available, loading from static imports for environment: ${env}`);

  const envConfig = await loadEnvironmentConfig(env);

  if (!envConfig) {
    const errorMsg = `[Config] ERROR: Environment config file for ${env} not found and translationsBaseUrl is required`;
    console.error(errorMsg);
    throw new Error(errorMsg);
  }

  config = transformConfig(envConfig);
  (config as RuntimeConfig & { __env?: string }).__env = env;

  applyLocalDevOverrides(config);

  if (!config?.translationsBaseUrl) {
    console.warn(`[Config] WARNING: translationsBaseUrl is missing from ${env} config, will use local translations`);
  }

  console.log(
    `[Config] ✓ Config loaded from ${env} environment file, baseUrl: ${config?.api?.baseUrl}, translationsBaseUrl: ${config?.translationsBaseUrl ?? "none (using local)"}`,
  );
  return config;
}

/**
 * Load runtime configuration
 * 
 * In production builds (CI/CD):
 * 1. First tries to fetch /config.json from server (matches CI/CD behavior)
 * 2. Falls back to static imports based on environment if fetch fails
 * 
 * In development mode:
 * Uses static imports based on environment (dev/test/uat/prod)
 */
export async function loadConfig(): Promise<RuntimeConfig> {
  config = null;

  const runtimeConfig = await fetchRuntimeConfig();
  return runtimeConfig
    ? handleServerConfig(runtimeConfig)
    : handleStaticConfig(getEnvironment());
}

/**
 * Get auth config for AuthProvider
 * @param runtimeConfig Optional runtime config to avoid duplicate loading
 */
export async function getAuthConfig(
  runtimeConfig?: RuntimeConfig,
): Promise<AuthConfig> {
  const config = runtimeConfig ?? (await loadConfig());

  return {
    issuer: config.okta.issuer,
    clientId: config.okta.clientId,
    redirectUri: config.okta.redirectUri,
    scopes: config.okta.scopes,
    // Default to false: Okta domains configured in trusted origins for direct API access
    // Can be overridden per environment via config if needed
    useProxy: config.okta.useProxy ?? false,
  };
}

/**
 * Get API base URL
 * Always returns the actual gateway URL (no proxy)
 * Note: Ensure the gateway has CORS configured for localhost if needed
 * @param runtimeConfig Optional runtime config to avoid duplicate loading
 */
export async function getApiBaseUrl(
  runtimeConfig?: RuntimeConfig,
): Promise<string> {
  const config = runtimeConfig ?? (await loadConfig());
  return config.api.baseUrl;
}
